import React, {useState, useEffect, useMemo} from 'react';
import { PlayCircleFilled, CheckCircle, VisibilityOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ReviewStatus from '../../../config/ReviewStatus.js';
import { DateUtils } from '../../../utils/DateUtils.js';
import TableFilterConfig from '../../../uiComponents/MuiTable/TableFilterConfig.js';
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader.js';
import MuiTablePagination from '../../../uiComponents/MuiTable/MuiTablePagination.js';
import {stableSort, getComparator} from '../../../uiComponents/MuiTable/MuiHelper.js';
import { 
  TableBody,
  TableContainer,
  Button,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js'
import QuickActionButtons from '../../../uiComponents/QuickActionButtons.js';
import { QUICK_ACTION_LABELS, quickActionsFilterConfig, withQuickActions } from '../../../utils/QuickActions.js';
import { VariantDetailsLink, GeneCell } from '../../../uiComponents/VariantCell.js';
import VariantCallDataModal from '../VariantCallDataModal/index.js';

const useStyles = makeStyles({
  // Compact modal-trigger action; hugs its content like the Settings button.
  viewButton: {
    padding: '5px 10px',
    lineHeight: 1.2,
    // Global 508 focus style sets border:none; keep a matching 1px border so the box doesn't shift.
    '&:focus-visible': {
      border: '1px solid var(--accent-primary-color)',
    },
  },
});

//Important: The id here must match the column name in the returned data. Label can be different for display.
const headCells = [
  { label: 'Quick Actions', id: 'quickActions', disablePadding: false },
  { label: 'Variant Name', id: 'variantName', numeric: false, disablePadding: false },
  { label: 'Latest Review Status', id: 'interpretationStatusString', numeric: false, disablePadding: false },
  { label: 'Gene', id: 'gene', numeric: false, disablePadding: false },
  { label: 'Condition', id: 'potentialCondition', numeric: false, disablePadding: false },
  { label: 'Last Reviewed', id: 'closedDate', numeric: false, disablePadding: false },
  { label: 'Variant Call Data', id: 'identifiedVariantSamples', numeric: false, disablePadding: false },
];

export default function PatientDetailsMolecularTable(props) {
  const classes = useStyles();
  const rows = props.data;
  const [tableData, setTableData] = useState(props.data);
  const [order, setOrder] = useState('desc'); //default sort direction
  const [orderBy, setOrderBy] = useState('closedDate'); //default sort column for table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [variantCallDataRow, setVariantCallDataRow] = useState(null);

  // Derive Quick Action labels per row so they can drive the Quick Action(s) filter options.
  const getQuickActionsForRow = (row) => {
    const actions = [];
    const assignment = props.assignedByVariantId?.[row.variantId];
    if (assignment && assignment.status !== 'Completed') {
      actions.push(QUICK_ACTION_LABELS.assigned);
    }
    if (row.reviewStatus === ReviewStatus.CLOSED) {
      actions.push(QUICK_ACTION_LABELS.consensusComplete);
    } else if (row.reviewStatus === ReviewStatus.READY_FOR_CONSENSUS) {
      actions.push(QUICK_ACTION_LABELS.readyForConsensus);
    } else if (row.reviewStatus === ReviewStatus.CONSENSUS_IN_PROGRESS) {
      actions.push(QUICK_ACTION_LABELS.consensusInProgress);
    }
    return actions;
  };

  // Attach a synthetic quickActions field so TableFilterConfig can offer the Quick Action(s) filter.
  const rowsWithQuickActions = useMemo(
    () => withQuickActions(props.data, getQuickActionsForRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.data, props.assignedByVariantId]
  );

  //intialize tableData with the Quick Actions-augmented rows so the Quick Actions column can sort
  useEffect(() => {
    setTableData(rowsWithQuickActions)
    // console.log("patient details table data: " + JSON.stringify(props.data));
  }, [rowsWithQuickActions] );

  useEffect(() => {
    //if upload was successful, sort by date desc
    if (props.sortTableByDateDesc) {
      setOrder('desc');
      setOrderBy('closedDate');
    }
  }, [props.sortTableByDateDesc]);

  const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
  };
  
  const handleChangePage = (event, newPage) => {
      setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
  };
  
  const statusCell = (row) => {
    if (row.reviewStatus === ReviewStatus.CLOSED) {
      return (
        <RouterLink
          to={`/variantconsensus/${row.variantId}`}
          state={{
            variantId: row.variantId,
            reviewStatus: row.reviewStatus,
            variantName: row.variantName,
            gene: row.gene,
            potentialCondition: row.potentialCondition,
            breadCrumbs: props.breadCrumbs,
          }}
        >
          Consensus review complete <CheckCircle style={{ verticalAlign: 'middle', fontSize: '1rem' }}/>
        </RouterLink>
      )
    } else if (row.reviewStatus === ReviewStatus.READY_FOR_CONSENSUS) {
      return (
        <RouterLink
          to={`/variantconsensus/${row.variantId}`}
          state={{
            variantId: row.variantId,
            reviewStatus: row.reviewStatus,
            variantName: row.variantName,
            gene: row.gene,
            potentialCondition: row.potentialCondition,
            breadCrumbs: props.breadCrumbs,
          }}
        >
          Ready for consensus review <PlayCircleFilled style={{ verticalAlign: 'middle', fontSize: '1rem' }}/>
        </RouterLink>
      )
    } else if (row.reviewStatus === ReviewStatus.CONSENSUS_IN_PROGRESS) {
      return (
        <RouterLink
          to={`/variantconsensus/${row.variantId}`}
          state={{
            variantId: row.variantId,
            reviewStatus: row.reviewStatus,
            variantName: row.variantName,
            gene: row.gene,
            potentialCondition: row.potentialCondition,
            breadCrumbs: props.breadCrumbs,
          }}
        >
          Consensus review in progress <CheckCircle style={{ verticalAlign: 'middle', fontSize: '1rem' }}/>
        </RouterLink>
      )
    } else {
      return (
        row.interpretationStatusString
      )
    }    
  }

  const variantCallDataCell = (row) => {
    if (!row.identifiedVariantSamples || row.identifiedVariantSamples.length === 0) {
      return null;
    }
    return (
      <Button
        className={classes.viewButton}
        variant="outlined"
        color="primary"
        aria-label="View variant call data"
        onClick={() => setVariantCallDataRow(row)}
      >
        <VisibilityOutlined fontSize="small" className="iconMarginRight" />View
      </Button>
    );
  }

  return (
    <>
      <div className="tableFilterGrid">
        <TableFilterConfig 
          rows={rowsWithQuickActions} 
          onFilteredDataChange={(filtered) => {
            setTableData(filtered);
            setPage(0);
          }}
          filterConfig={[
            { type: 'gene', label: 'Gene(s)', field: 'gene' },
            { type: 'condition', label: 'Condition(s)', field: 'potentialCondition' },
            quickActionsFilterConfig,
          ]}
        />
      </div>
      <div>
        <StyledPaper>
          <TableContainer>
            <StyledTable
              size='small'
              aria-label="patient details table" >
              <MuiTableHeader 
                headCells={headCells} 
                order={order}  
                orderBy={orderBy} 
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(tableData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                  return (
                    <StyledTableRow tabIndex={-1} key={index}>
                      <StyledTableCell style={{width: '16%'}}>
                        <QuickActionButtons
                          row={row}
                          assignment={props.assignedByVariantId?.[row.variantId]}
                          breadCrumbs={props.breadCrumbs}
                          programId={props.programId}
                        />
                      </StyledTableCell>
                      <StyledTableCell style={{width: '16%'}}><VariantDetailsLink value={row.variantName} row={row} breadCrumbs={props.breadCrumbs} /></StyledTableCell>
                      <StyledTableCell style={{width: '16%'}}>{statusCell(row)}</StyledTableCell> 
                      <StyledTableCell className='gene' style={{width: '10%'}}><GeneCell gene={row.gene} /></StyledTableCell>
                      <StyledTableCell style={{width: '10%'}}>{row.potentialCondition}</StyledTableCell>
                      <StyledTableCell style={{width: '10%'}}>{DateUtils.FormatLocalDate(row.closedDate)}</StyledTableCell>
                      <StyledTableCell style={{width: '20%'}}>{variantCallDataCell(row)}</StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </StyledTable>
          </TableContainer>
          <MuiTablePagination 
            rowsPerPage={rowsPerPage}
            page={page}
            count={tableData.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage} 
          />
        </StyledPaper>
      </div>
      <VariantCallDataModal
        open={Boolean(variantCallDataRow)}
        onClose={() => setVariantCallDataRow(null)}
        variantName={variantCallDataRow?.variantName}
        chromosomalHGVS={variantCallDataRow?.chromosomalHGVS}
        dateIdentified={variantCallDataRow?.dateLastIdentified}
        sampleData={variantCallDataRow?.identifiedVariantSamples}
      />
    </>
  );
}
